import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { WithServiceId } from '@mews-commander/types';
import { getRepositoryQueryKey, SetSellLimitParameters } from '@mews-commander/data-access';
import { BusinessError, createZodFormResolver, lt, showSuccessAlert, useFormControls } from '@mews/framework';
import { Day } from '@mews/enums';
import { useDialogs } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { daysArrayToDaysParameters } from '@mews-commander/utils';
import { SellLimitFieldNames } from '../components/fieldNames';
import { AddSellLimitFormModel, addSellLimitFormSchema } from '../schemas';
import { useSetSellLimit } from '../queries';
import { ADD_SELL_LIMIT_DIALOG_NAME } from '../constants';
import { useSellLimitsConfirmationDialog } from './useSellLimitsConfirmationDialog';

export const useAddSellLimitFormControls = ({ serviceId }: WithServiceId) => {
    const { mutate, isPending } = useSetSellLimit();

    const queryClient = useQueryClient();
    const { closeDialog } = useDialogs();

    const valuesRef = useRef<AddSellLimitFormModel | undefined>();

    const applySellLimit = () => {
        if (!valuesRef.current) {
            throw new BusinessError('Values are not stored.');
        }

        const params = addSellLimitFormSchema
            .transform(
                ({ days, rateSelectOption, spaceSelectOption, ...rest }) =>
                    ({
                        serviceId,
                        rateGroupIds: rateSelectOption.type === 'rateGroup' ? rateSelectOption.rateGroupIds : [],
                        rateIds: rateSelectOption.type === 'exactRate' ? rateSelectOption.exactRateIds : [],
                        resourceCategoryIds:
                            spaceSelectOption.type === 'resourceCategory' ? spaceSelectOption.resourceCategoryIds : [],
                        days: daysArrayToDaysParameters(days),
                        ...rest,
                    }) satisfies SetSellLimitParameters
            )
            .parse(valuesRef.current);
        mutate(params, {
            onSuccess: async () => {
                closeDialog(ADD_SELL_LIMIT_DIALOG_NAME);
                showSuccessAlert({
                    title: lt('SellLimitsAppliedSuccessMessage'),
                });
                await queryClient.invalidateQueries({ queryKey: getRepositoryQueryKey('sellLimits', 'get') });
                valuesRef.current = undefined;
            },
        });
    };

    const confirmApplySellLimits = useSellLimitsConfirmationDialog({
        onConfirm: applySellLimit,
        primaryActionKey: TextKey.Apply,
        dialogTitleKey: TextKey.ApplySellLimitsConfirmationTitle,
        dialogBodyKey: TextKey.ApplySellLimitsConfirmationDescription,
    });

    const onSubmit = (formValues: AddSellLimitFormModel) => {
        valuesRef.current = formValues;
        confirmApplySellLimits();
    };

    const formControls = useFormControls<AddSellLimitFormModel>({
        onSubmit,
        defaultValues,
        resolver,
        shouldUnregister: true,
    });

    return {
        ...formControls,
        isSubmitting: isPending,
    };
};

const resolver = createZodFormResolver<AddSellLimitFormModel>(addSellLimitFormSchema);

const defaultValues = {
    [SellLimitFieldNames.DAYS_FIELD_NAME]: Day.enumValues.map((day) => day.name),
    [SellLimitFieldNames.NUMBER_OF_SPACES_FIELD_NAME]: 0,
    [SellLimitFieldNames.RATE_SELECT_OPTION_FIELD_NAME]: {
        type: 'exactRate' as const,
    },
    [SellLimitFieldNames.SPACE_SELECT_OPTION_FIELD_NAME]: {
        type: 'allSpaces' as const,
    },
};
